import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { θProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';
import { ClientInsurance } from '@sondermind/utilities/models-client-insurance';
import { IAPIListRequestOptions } from '@sondermind/utilities/models-http';

import { ClientInsuranceHttpService } from '../services/client-insurance.httpservice';
import { ITempImprovementPatientInsurances } from '../models/temp-improvement-patient-insurances.interface';

// NOTE: (PE-6755) full refactor to use store in all palces is out of scope for his ticket work.
// this meant the start of the store to be updated when convenient.
@Injectable({
  providedIn: 'root'
})
export class InsuranceStore {
  private fullListSubject: BehaviorSubject<ClientInsurance[]> = new BehaviorSubject([]);
  private get fullList$(): Observable<ClientInsurance[]> {
    return this.fullListSubject.asObservable();
  }

  // Temp Improvement data - new endpoint with additional properties
  private fullListTempImprovementSubject: BehaviorSubject<ITempImprovementPatientInsurances[]> =
    new BehaviorSubject([]);
  get fullListTempImprovement$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullListTempImprovementSubject.asObservable();
  }

  /**
   * Return EAP insurance list.
   */
  get eapList$(): Observable<ClientInsurance[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.provider_network_company?.eap))
    );
  }

  /**
   * Return Medicare insurance list.
   */
  get medicareList$(): Observable<ClientInsurance[]> {
    return this.insuranceList$.pipe(
      map((ins) => ins.filter((i) => i.provider_network_company?.medicare || i.provider_network_company?.medicare_advantage))
    );
  }

  /**
   * Return non-EAP and non-self-pay insurance list.
   */
  get insuranceList$(): Observable<ClientInsurance[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => this.isRegularInsurance(i)))
    );
  }

  /**
   * Return PNCs that also includes self pay.
   */
  get insuranceListWithSelfPay$(): Observable<ClientInsurance[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.provider_network_company
        && !i.provider_network_company.eap))
    );
  }

  /**
   * Return self pay list
   */
  get selfPayList$(): Observable<ClientInsurance[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.provider_network_company?.is_snap
        && i.provider_network_company?.average_client_responsibility?.length > 0))
    );
  }

  get compSessionList$(): Observable<ClientInsurance[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.is_usage_based))
    );
  }

  constructor(
    private clientInsuranceHttpService: ClientInsuranceHttpService
  ) {
    this.loadInsuranceList();
  }

  /**
   * Load list of insurances for client account.
   */
  loadInsuranceList(): void {
    this.clientInsuranceHttpService.list().subscribe({
      next: (resp) => {
        this.fullListSubject.next(resp);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  /**
   * Load all insurances
   * Create a observable pattern in order to subscribe to the result on other components
   * @param opts - Optional request options including view parameter for detailed data
   * @returns voided observable
   */
  loadInsuranceList$(opts?: Partial<IAPIListRequestOptions>): Observable<void> {
    // If opts with view parameter is provided, use the temp improvement endpoint for detailed data
    if (opts && opts.view) {
      return this.clientInsuranceHttpService.listTempImprovement(opts).pipe(
        map((resp) => {
          this.fullListTempImprovementSubject.next(resp);
          return undefined;
        })
      );
    }

    // Otherwise use the standard endpoint
    return this.clientInsuranceHttpService.list().pipe(
      map((resp) => {
        this.fullListSubject.next(resp);

        return undefined;
      })
    );
  }

  /**
   * Use to refresh store and check if we need to send User to mulitpayor screen
   * in insurance page component.
   */
  loadAndCheckMultipayor(): Observable<boolean> {
    return this.clientInsuranceHttpService.list().pipe(
      switchMap(
        (resp) => {
          this.fullListSubject.next(resp);
          const insuranceList = resp.filter((i) => this.isRegularInsurance(i));
          return of(insuranceList.length > 1);
        }
      )
    );
  }

  isInsuranceListEmpty$(): Observable<boolean> {
    return this.insuranceListWithSelfPay$.pipe(
      map((insList) => insList.length === 0)
    );
  }

  /**
   * Remove single insurance record from client account.
   */
  deleteInsurance(ins: ClientInsurance): void {
    this.clientInsuranceHttpService.delete(ins.id).subscribe({
      next: () => {
        const updatedList = this.fullListSubject.getValue().filter((insurance) => insurance.id !== ins.id);
        this.fullListSubject.next(updatedList);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  /**
   * Remove multiple insurance records from client account.
   */
  deleteMultipleInsurances(insurances: ClientInsurance[]): void {
    const insuranceIds = insurances.map((ins) => ins.id.toString());
    this.clientInsuranceHttpService.deleteMultiple(insuranceIds).subscribe({
      next: () => {
        const updatedList = this.fullListSubject.getValue().filter((insurance) => !insuranceIds.includes(
          insurance.id.toString()
        ));
        this.fullListSubject.next(updatedList);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  /**
   * Check if regular (non-eap, non-self-pay) is returned.
   */
  isRegularInsurance(ins: ClientInsurance): boolean {
    return ins.provider_network_company
      && !ins.provider_network_company.is_snap
      && !ins.provider_network_company.eap
      && !ins.is_usage_based;
  }

  getPncByState(pncList: ClientInsurance[], location: string): θProviderNetworkCompany | null {
    const stateClientInsurance = pncList.find((pnc: ClientInsurance) => pnc.provider_network_company.network_state === location);

    return stateClientInsurance?.provider_network_company || null;
  }

  // ============================================================================
  // Temp Improvement Methods
  // TODO: Remove older methods when everything is migrated to the temp improvement endpoint
  // ============================================================================

  /**
   * Load all insurances using temp improvement endpoint.
   * Create a observable pattern in order to subscribe to the result on other components
   * @param opts - Optional request options including view parameter
   * @returns voided observable
   */
  loadInsuranceListTempImprovement$(opts: Partial<IAPIListRequestOptions> = {}): Observable<void> {
    return this.clientInsuranceHttpService.listTempImprovement(opts).pipe(
      map((resp) => {
        this.fullListTempImprovementSubject.next(resp);

        return undefined;
      })
    );
  }
}
